import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { protocolApi } from "@web/api/protocol-api";
import { protocolDeploymentMatches } from "@web/api/deployment-guard";
import {
  createProtocolTransactions,
  type TransactionProgress,
} from "@web/api/protocol-transactions";
import { BrokersView } from "@web/features/terminal/BrokersView";
import { amountLabel, compactAddress } from "@web/features/terminal/presentation";
import { PersonalRewardsView } from "@web/features/terminal/PersonalRewardsView";
import { RewardsView } from "@web/features/terminal/RewardsView";
import { TerminalView } from "@web/features/terminal/TerminalView";
import { LoansView } from "@web/features/terminal/LoansView";
import { AppPageHeader } from "@web/features/site/AppPageHeader";
import { CollectionPage } from "@web/features/site/CollectionPage";
import { LandingPage } from "@web/features/site/LandingPage";
import { ProtocolPage } from "@web/features/site/ProtocolPage";
import { SiteHeader } from "@web/features/site/SiteHeader";
import type {
  AccountSnapshot,
  ActivitySnapshot,
  AmmSpecificQuote,
  BrokerSnapshot,
  IndexedLoanPage,
  LoanLiveSnapshot,
  NftMetadata,
  ProtocolSnapshot,
} from "@stonkland/protocol/api-types";
import { targetChain } from "@stonkland/protocol/chain";
import { addresses } from "@stonkland/protocol/contracts";
import { publicEnvironment } from "@stonkland/protocol/public-env";
import { applicationRoutes, useRoute } from "@web/routes";
import {
  isUserRejectedError,
  readableError,
} from "@web/toasts/error-message";
import { useToasts } from "@web/toasts/toast";
import { useWalletSession } from "@web/wallet/session";

function transactionHash(result: unknown) {
  if (typeof result === "string" && /^0x[0-9a-f]{64}$/i.test(result)) {
    return result;
  }
  if (
    result &&
    typeof result === "object" &&
    "hash" in result &&
    typeof result.hash === "string" &&
    /^0x[0-9a-f]{64}$/i.test(result.hash)
  ) {
    return result.hash;
  }
  return undefined;
}

const prelaunch = publicEnvironment("VITE_PRELAUNCH") === "true";

export default function StonklandApp() {
  const { route, navigate } = useRoute();
  const applicationRoute = applicationRoutes.includes(route);
  const { show: showToast, update: updateToast } = useToasts();
  const {
    account,
    chainId: walletChainId,
    configured: walletConfigured,
    openAccount,
    openConnect,
    provider: walletProvider,
    status: walletStatus,
    switchToTargetNetwork,
  } = useWalletSession();
  const [protocol, setProtocol] = useState<ProtocolSnapshot>();
  const [accountSnapshot, setAccountSnapshot] = useState<AccountSnapshot>();
  const [broker, setBroker] = useState<BrokerSnapshot>();
  const [activity, setActivity] = useState<ActivitySnapshot>();
  const [loans, setLoans] = useState<IndexedLoanPage>();
  const [selectedToken, setSelectedToken] = useState<bigint>();
  const [selectedTier, setSelectedTier] = useState(1);
  const [batchTokens, setBatchTokens] = useState<bigint[]>([]);
  const [snipeToken, setSnipeToken] = useState("");
  const [snipeQuote, setSnipeQuote] = useState<AmmSpecificQuote>();
  const [snipeMetadata, setSnipeMetadata] = useState<NftMetadata>();
  const [brokerMetadata, setBrokerMetadata] = useState<NftMetadata>();
  const [loanDurationDays, setLoanDurationDays] = useState(365);
  const [loanId, setLoanId] = useState("");
  const [liveLoans, setLiveLoans] = useState<Record<string, LoanLiveSnapshot>>(
    {},
  );
  const [busy, setBusy] = useState<string>();
  const activeTransactionToastId = useRef<string | undefined>(undefined);
  const activeTransactionStep = useRef<
    { hash: `0x${string}`; label: string } | undefined
  >(undefined);
  const walletSnapshot = useRef<{
    account?: string;
    chainId?: number;
    initialized: boolean;
  }>({ initialized: false });

  const reportTransactionProgress = useCallback(
    (toastId: string, progress: TransactionProgress) => {
      if (progress.hash) {
        activeTransactionStep.current = {
          hash: progress.hash,
          label: progress.label,
        };
      }
      const action = progress.hash
        ? {
            href: `${targetChain.blockExplorers.default.url}/tx/${progress.hash}`,
            label: "View transaction",
          }
        : null;
      if (progress.stage === "awaiting_signature") {
        updateToast(toastId, {
          action: null,
          description: "Review and confirm the request in your wallet.",
          durationMs: 0,
          title: progress.label,
          variant: "loading",
        });
        return;
      }
      updateToast(toastId, {
        action,
        description:
          progress.stage === "submitted"
            ? "Submitted to Robinhood Chain. Waiting for confirmation."
            : "Confirmed onchain. Preparing the next step.",
        durationMs: 0,
        title:
          progress.stage === "submitted"
            ? `${progress.label} submitted`
            : `${progress.label} confirmed`,
        variant: "loading",
      });
    },
    [updateToast],
  );

  const transactions = useMemo(
    () =>
      walletProvider &&
      account &&
      walletChainId === targetChain.id &&
      protocol?.chainId === targetChain.id &&
      Object.entries(addresses).every(
        ([key, address]) =>
          address &&
          protocol.contracts[key]?.toLowerCase() === address.toLowerCase(),
      )
        ? createProtocolTransactions(walletProvider, account)
        : undefined,
    [walletProvider, account, walletChainId, protocol],
  );

  const deploymentMatches = useMemo(
    () => protocolDeploymentMatches(protocol, targetChain.id, addresses),
    [protocol],
  );
  const protocolLive = Boolean(
    protocol?.live && protocol.metrics && deploymentMatches,
  );
  const transactionReady = Boolean(
    protocolLive &&
      account &&
      walletProvider &&
      walletChainId === targetChain.id &&
      !busy,
  );
  const metrics = protocol?.metrics || undefined;
  const ownedTokens = useMemo(
    () => (accountSnapshot?.tokenIds || []).map(BigInt),
    [accountSnapshot],
  );
  const landBalance = accountSnapshot
    ? BigInt(accountSnapshot.landBalance)
    : undefined;
  const ammPrice = metrics ? BigInt(metrics.ammPrice) : undefined;
  const clockInPot = metrics ? BigInt(metrics.clockInPot) : undefined;
  const clockInMinimum = metrics ? BigInt(metrics.clockInMinimum) : undefined;
  const overtimePot = metrics ? BigInt(metrics.overtimePot) : undefined;
  const overtimeMinimum = metrics ? BigInt(metrics.overtimeMinimum) : undefined;
  const activationTier = broker?.activation.tier || 0;
  const portfolioAccount = broker?.account;
  const currentTier = protocol?.tiers.find(
    (tier) => tier.tier === selectedTier,
  );

  const notifyError = useCallback(
    (title: string, error: unknown, fallback: string, dedupeKey?: string) =>
      showToast({
        dedupeKey,
        description: readableError(error, fallback),
        title,
        variant: "error",
      }),
    [showToast],
  );

  const notifyWarning = useCallback(
    (title: string, description: string, dedupeKey?: string) =>
      showToast({
        dedupeKey,
        description,
        title,
        variant: "warning",
      }),
    [showToast],
  );

  const refreshBroker = useCallback(async () => {
    if (!protocolLive || !selectedToken) {
      setBroker(undefined);
      return;
    }
    try {
      const [brokerBody, metadataBody] = await Promise.all([
        protocolApi.broker(selectedToken),
        protocolApi.metadata(selectedToken),
      ]);
      setBroker(brokerBody);
      setBrokerMetadata(metadataBody);
    } catch (error) {
      setBroker(undefined);
      setBrokerMetadata(undefined);
      notifyError(
        "Broker data unavailable",
        error,
        "Could not load this Broker.",
        `broker-read-${selectedToken}`,
      );
    }
  }, [notifyError, protocolLive, selectedToken]);

  useEffect(() => {
    let cancelled = false;
    const eligible = new Set(
      ownedTokens
        .filter(
          (tokenId) =>
            (accountSnapshot?.activationTiers[tokenId.toString()] || 0) <
            selectedTier,
        )
        .map(String),
    );
    queueMicrotask(() => {
      if (!cancelled) {
        setBatchTokens((current) =>
          current.filter((tokenId) => eligible.has(tokenId.toString())),
        );
      }
    });
    return () => {
      cancelled = true;
    };
  }, [accountSnapshot, ownedTokens, selectedTier]);

  useEffect(() => {
    const numeric = Number(snipeToken);
    if (
      !protocolLive ||
      !Number.isSafeInteger(numeric) ||
      numeric < 1 ||
      numeric > 4_444
    ) {
      let cancelled = false;
      queueMicrotask(() => {
        if (!cancelled) {
          setSnipeQuote(undefined);
          setSnipeMetadata(undefined);
        }
      });
      return () => {
        cancelled = true;
      };
    }
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      void Promise.all([
        protocolApi.specificBuyQuote(BigInt(numeric), controller.signal),
        protocolApi.metadata(BigInt(numeric), controller.signal),
      ])
        .then(([quote, metadata]) => {
          setSnipeQuote(quote);
          setSnipeMetadata(metadata);
        })
        .catch((error: unknown) => {
          if (error instanceof DOMException && error.name === "AbortError")
            return;
          setSnipeQuote(undefined);
          setSnipeMetadata(undefined);
          notifyError(
            "Exact-ID quote unavailable",
            error,
            "Could not load the current Broker inventory quote.",
            "snipe-quote-read",
          );
        });
    }, 200);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [notifyError, protocolLive, snipeToken]);

  useEffect(() => {
    const ids = [
      ...(loans?.loans || []).map((loan) => loan.loanId),
      ...(/^\d+$/.test(loanId) && loanId !== "0" ? [loanId] : []),
    ].filter((id, index, all) => all.indexOf(id) === index);
    if (!protocolLive || ids.length === 0) {
      let cancelled = false;
      queueMicrotask(() => {
        if (!cancelled) setLiveLoans({});
      });
      return () => {
        cancelled = true;
      };
    }
    const controller = new AbortController();
    void protocolApi
      .liveLoans(ids, controller.signal)
      .then((snapshots) =>
        setLiveLoans(
          Object.fromEntries(
            snapshots.map((snapshot) => [snapshot.loanId, snapshot]),
          ),
        ),
      )
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError")
          return;
        setLiveLoans({});
        notifyError(
          "Live loan data unavailable",
          error,
          "Could not refresh current loan deadlines and fees.",
          "live-loans-read",
        );
      });
    return () => controller.abort();
  }, [loanId, loans, notifyError, protocolLive]);

  const refreshAll = useCallback(async () => {
    if (prelaunch) {
      setProtocol(undefined);
      setActivity(undefined);
      setLoans(undefined);
      setAccountSnapshot(undefined);
      setSelectedToken(undefined);
      setBroker(undefined);
      if (applicationRoute) {
        notifyWarning(
          "Robinhood launch pending",
          "Contracts and live data will be enabled together after deployment.",
          "protocol-prelaunch",
        );
      }
      return;
    }

    try {
      setBusy("refresh");
      const protocolBody = await protocolApi.protocol();
      const expectedDeployment = protocolDeploymentMatches(
        protocolBody,
        targetChain.id,
        addresses,
      );
      if (!protocolBody.live || !protocolBody.metrics || !expectedDeployment) {
        // Never retain or render data from a retired chain or contract set. The
        // public API hostname can be updated independently from the static site,
        // so identity is verified before any dependent endpoint is queried.
        setProtocol(undefined);
        setActivity(undefined);
        setLoans(undefined);
        setAccountSnapshot(undefined);
        setSelectedToken(undefined);
        setBroker(undefined);
        if (applicationRoute) {
          notifyWarning(
            "Protocol unavailable",
            expectedDeployment
              ? protocolBody.reason || "The Robinhood deployment is offline."
              : "The API is not serving the configured Robinhood deployment.",
            "protocol-unavailable",
          );
        }
        return;
      }

      setProtocol(protocolBody);
      setSelectedTier((current) => {
        if (
          protocolBody.tiers.some(
            (tier) => tier.tier === current && tier.enabled,
          )
        ) {
          return current;
        }
        return protocolBody.tiers.find((tier) => tier.enabled)?.tier || 1;
      });

      if (!applicationRoute) return;

      const [activityResult, loansResult] = await Promise.allSettled([
        protocolApi.activity(20),
        protocolApi.loans(20),
      ]);
      setActivity(
        activityResult.status === "fulfilled"
          ? activityResult.value
          : undefined,
      );
      setLoans(
        loansResult.status === "fulfilled" ? loansResult.value : undefined,
      );
      if (activityResult.status === "rejected") {
        notifyError(
          "Activity index unavailable",
          activityResult.reason,
          "Could not load confirmed protocol activity.",
          "activity-read",
        );
      }
      if (loansResult.status === "rejected") {
        notifyError(
          "Loan index unavailable",
          loansResult.reason,
          "Could not load indexed loans.",
          "loans-read",
        );
      }

      if (!account) {
        setAccountSnapshot(undefined);
        setSelectedToken(undefined);
        setBroker(undefined);
        return;
      }

      const accountBody = await protocolApi.account(account);
      setAccountSnapshot(accountBody);
      const ids = accountBody.tokenIds.map(BigInt);
      setSelectedToken((current) =>
        current && ids.some((id) => id === current) ? current : ids[0],
      );
    } catch (error) {
      notifyError(
        "Live data refresh failed",
        error,
        "Could not refresh protocol data.",
        "protocol-refresh",
      );
    } finally {
      setBusy(undefined);
    }
  }, [account, applicationRoute, notifyError, notifyWarning]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void refreshAll();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [applicationRoute, refreshAll]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void refreshBroker();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [refreshBroker]);

  const handleWalletAction = useCallback(async () => {
    if (!walletConfigured) {
      notifyWarning(
        "Wallet connection unavailable",
        "Set VITE_REOWN_PROJECT_ID for this deployment.",
        "wallet-not-configured",
      );
      return;
    }

    try {
      setBusy("wallet");
      if (!account) {
        await openConnect();
        return;
      }
      if (walletChainId !== targetChain.id) {
        await switchToTargetNetwork();
        return;
      }
      await openAccount();
    } catch (error) {
      notifyError(
        "Wallet action failed",
        error,
        "The wallet action could not be completed.",
      );
    } finally {
      setBusy(undefined);
    }
  }, [
    account,
    notifyError,
    notifyWarning,
    openAccount,
    openConnect,
    switchToTargetNetwork,
    walletChainId,
    walletConfigured,
  ]);

  useEffect(() => {
    let cancelled = false;
    queueMicrotask(() => {
      if (cancelled) return;
      setAccountSnapshot(undefined);
      setSelectedToken(undefined);
      setBroker(undefined);
      setBrokerMetadata(undefined);
      setBatchTokens([]);
    });
    return () => {
      cancelled = true;
    };
  }, [account]);

  useEffect(() => {
    const previous = walletSnapshot.current;
    if (!previous.initialized) {
      walletSnapshot.current = {
        account,
        chainId: walletChainId,
        initialized: true,
      };
      return;
    }

    if (previous.account !== account) {
      if (account) {
        showToast({
          description: `${compactAddress(account)} is active on ${targetChain.name}.`,
          title: previous.account ? "Wallet account changed" : "Wallet connected",
          variant: "success",
        });
      } else if (previous.account) {
        showToast({
          description: "The wallet session has been disconnected.",
          title: "Wallet disconnected",
          variant: "info",
        });
      }
    } else if (account && previous.chainId !== walletChainId) {
      showToast({
        description:
          walletChainId === targetChain.id
            ? `Transactions are enabled on ${targetChain.name}.`
            : `Switch back to ${targetChain.name} before submitting a transaction.`,
        title:
          walletChainId === targetChain.id ? "Network ready" : "Unsupported network",
        variant: walletChainId === targetChain.id ? "success" : "warning",
      });
    }

    walletSnapshot.current = {
      account,
      chainId: walletChainId,
      initialized: true,
    };
  }, [account, showToast, walletChainId]);

  const requireLiveProtocol = () => {
    if (!protocolLive) {
      notifyWarning(
        "Protocol unavailable",
        protocol?.reason || "No transaction has been submitted.",
        "protocol-transaction-unavailable",
      );
      return false;
    }
    if (!walletConfigured) {
      notifyWarning(
        "Wallet connection unavailable",
        "Set VITE_REOWN_PROJECT_ID for this deployment.",
        "wallet-not-configured",
      );
      return false;
    }
    if (!account) {
      notifyWarning(
        "Wallet required",
        "Connect a wallet before submitting a transaction.",
        "wallet-required",
      );
      return false;
    }
    if (walletChainId !== targetChain.id) {
      notifyWarning(
        "Wrong network",
        `Switch your wallet to ${targetChain.name} first.`,
        "wrong-network",
      );
      return false;
    }
    if (!walletProvider || !transactions) {
      notifyWarning(
        "Wallet is still loading",
        "Wait for the connected provider to finish initializing.",
        "wallet-provider-loading",
      );
      return false;
    }
    return true;
  };

  const requireSelectedBroker = () => {
    if (!requireLiveProtocol()) return false;
    if (!selectedToken || !broker) {
      notifyWarning(
        "Select a Broker",
        "Choose a Broker owned by the connected wallet.",
      );
      return false;
    }
    return true;
  };

  const executeTransaction = async <Result,>(
    options: {
      busyKey: string;
      pendingDescription?: string;
      pendingTitle: string;
      successDescription: (result: Result) => string;
      successTitle: string;
    },
    action: () => Promise<Result>,
  ) => {
    if (activeTransactionToastId.current) {
      notifyWarning(
        "Transaction already in progress",
        "Wait for the current wallet transaction to finish.",
        "transaction-in-progress",
      );
      return undefined;
    }

    const toastId = showToast({
      description:
        options.pendingDescription ||
        "Preparing current onchain values before requesting a signature.",
      durationMs: 0,
      title: options.pendingTitle,
      variant: "loading",
    });
    activeTransactionToastId.current = toastId;
    activeTransactionStep.current = undefined;
    transactions?.setProgressReporter((progress) =>
      reportTransactionProgress(toastId, progress),
    );
    setBusy(options.busyKey);

    try {
      const result = await action();
      const hash = transactionHash(result);
      updateToast(toastId, {
        action: hash
          ? {
              href: `${targetChain.blockExplorers.default.url}/tx/${hash}`,
              label: "View confirmed transaction",
            }
          : null,
        description: options.successDescription(result),
        durationMs: 8_500,
        title: options.successTitle,
        variant: "success",
      });
      return result;
    } catch (error) {
      const rejected = isUserRejectedError(error);
      const submittedStep = activeTransactionStep.current as
        | { hash: `0x${string}`; label: string }
        | undefined;
      const description = readableError(
        error,
        rejected
          ? "The request was rejected in the wallet."
          : "The transaction could not be completed.",
      );
      updateToast(toastId, {
        action: submittedStep
          ? {
              href: `${targetChain.blockExplorers.default.url}/tx/${submittedStep.hash}`,
              label: `View ${submittedStep.label}`,
            }
          : null,
        description:
          rejected && submittedStep
            ? `${description} The earlier ${submittedStep.label.toLowerCase()} step remains submitted onchain.`
            : description,
        durationMs: rejected ? 6_500 : 10_000,
        title: rejected
          ? `${options.pendingTitle} cancelled`
          : `${options.pendingTitle} failed`,
        variant: rejected ? "info" : "error",
      });
      return undefined;
    } finally {
      if (activeTransactionToastId.current === toastId) {
        activeTransactionToastId.current = undefined;
      }
      activeTransactionStep.current = undefined;
      transactions?.setProgressReporter(undefined);
      setBusy(undefined);
    }
  };

  const activate = async () => {
    if (
      !requireSelectedBroker() ||
      !transactions ||
      !selectedToken ||
      !currentTier
    ) {
      return;
    }
    if (!currentTier?.enabled) {
      notifyWarning(
        "Tier unavailable",
        "That work tier is not enabled onchain.",
      );
      return;
    }
    if (activationTier && selectedTier <= activationTier) {
      notifyWarning(
        "Higher tier required",
        "Choose a higher enabled tier to upgrade this Broker.",
      );
      return;
    }

    const result = await executeTransaction(
      {
        busyKey: "activate",
        pendingTitle: activationTier
          ? `Upgrade Broker #${selectedToken}`
          : `Send Broker #${selectedToken} to work`,
        successDescription: () =>
          activationTier
          ? `Broker #${selectedToken} was upgraded to tier ${selectedTier}.`
          : `Broker #${selectedToken} was sent to work at tier ${selectedTier}.`,
        successTitle: activationTier
          ? "Broker upgraded"
          : "Broker is working",
      },
      () =>
        transactions.assignWork({
          tokenId: selectedToken,
          tier: currentTier.contractTier,
        }),
    );
    if (!result) return;
    await Promise.all([refreshAll(), refreshBroker()]);
  };

  const activateBatch = async () => {
    if (!requireLiveProtocol() || !transactions || !currentTier) return;
    if (batchTokens.length === 0) {
      notifyWarning(
        "No Brokers selected",
        "Select at least one eligible Broker for the batch.",
      );
      return;
    }
    const tokenCount = batchTokens.length;
    const result = await executeTransaction(
      {
        busyKey: "activate-batch",
        pendingTitle: `Send ${tokenCount} Brokers to work`,
        successDescription: (confirmed) =>
          `${tokenCount} Brokers moved to tier ${selectedTier} for ${amountLabel(confirmed.totalFee, 0)} LAND in one transaction.`,
        successTitle: "Batch assignment confirmed",
      },
      () =>
        transactions.assignWorkBatch({
          tokenIds: batchTokens,
          tier: currentTier.contractTier,
        }),
    );
    if (!result) return;
    setBatchTokens([]);
    await Promise.all([refreshAll(), refreshBroker()]);
  };

  const buyFromAmm = async (exactTokenId?: bigint) => {
    if (!requireLiveProtocol() || !transactions) {
      return;
    }
    const result = await executeTransaction(
      {
        busyKey: exactTokenId ? "snipe" : "buy-amm",
        pendingTitle: exactTokenId
          ? `Buy Broker #${exactTokenId}`
          : "Buy next Broker",
        successDescription: (confirmed) => {
          const acquiredTokenId =
            exactTokenId ||
            ("tokenId" in confirmed ? confirmed.tokenId : undefined);
          return `Broker #${acquiredTokenId} bought for ${amountLabel(confirmed.tokenCost, 0)} LAND + ${amountLabel(confirmed.nativeFee, 5)} ETH.`;
        },
        successTitle: "Broker purchase confirmed",
      },
      () => transactions.buyBroker({ tokenId: exactTokenId }),
    );
    if (!result) return;
    await refreshAll();
  };

  const buyLand = async (options: {
    amountIn: bigint;
    slippageBps: number;
  }) => {
    if (!requireLiveProtocol() || !transactions) return false;
    const result = await executeTransaction(
      {
        busyKey: "buy-land",
        pendingDescription:
          "Refreshing the Uniswap v4 quote before requesting your signature.",
        pendingTitle: "Buy LAND with ETH",
        successDescription: (confirmed) =>
          `${amountLabel(confirmed.amountIn, 6)} ETH swapped for at least ${amountLabel(confirmed.minimumAmountOut, 2)} LAND with ${confirmed.slippageBps / 100}% maximum slippage.`,
        successTitle: "LAND purchase confirmed",
      },
      () => transactions.buyLand(options),
    );
    if (!result) return false;
    await refreshAll();
    return true;
  };

  const sellLand = async (options: {
    amountIn: bigint;
    slippageBps: number;
  }) => {
    if (!requireLiveProtocol() || !transactions) return false;
    const result = await executeTransaction(
      {
        busyKey: "sell-land",
        pendingDescription:
          "Approving the exact LAND amount, then refreshing the Uniswap v4 quote before the sale.",
        pendingTitle: "Sell LAND for ETH",
        successDescription: (confirmed) =>
          `${amountLabel(confirmed.amountIn, 2)} LAND swapped for at least ${amountLabel(confirmed.minimumAmountOut, 6)} ETH with ${confirmed.slippageBps / 100}% maximum slippage.`,
        successTitle: "LAND sale confirmed",
      },
      () => transactions.sellLand(options),
    );
    if (!result) return false;
    await refreshAll();
    return true;
  };

  const sellToAmm = async () => {
    if (!requireSelectedBroker() || !transactions || !selectedToken) {
      return;
    }
    const result = await executeTransaction(
      {
        busyKey: "sell-amm",
        pendingTitle: `Sell Broker #${selectedToken}`,
        successDescription: (confirmed) =>
          `Broker #${selectedToken} sold for ${amountLabel(confirmed.tokenPayout, 0)} LAND after a fresh ${amountLabel(confirmed.nativeFee, 5)} ETH fee quote.`,
        successTitle: "Broker sale confirmed",
      },
      () => transactions.sellBroker({ tokenId: selectedToken }),
    );
    if (!result) return;
    await refreshAll();
  };

  const clockIn = async () => {
    if (!requireLiveProtocol() || !transactions) {
      return;
    }
    const roundWasActive = metrics?.clockInActive;
    const result = await executeTransaction(
      {
        busyKey: "clock-in",
        pendingTitle: roundWasActive
          ? `Continue Clock In round ${metrics?.clockInRound}`
          : "Start Clock In round",
        successDescription: (confirmed) =>
          roundWasActive
          ? `Clock In round ${metrics.clockInRound} continued from its stored onchain cursor.`
          : `${amountLabel(confirmed.nativeAmount, 5)} ETH converted across ${confirmed.routeCount} onchain routes.`,
        successTitle: roundWasActive
          ? "Clock In distribution continued"
          : "Clock In round started",
      },
      () => transactions.clockIn(),
    );
    if (!result) return;
    await refreshAll();
  };

  const runOvertime = async () => {
    if (!requireLiveProtocol() || !transactions) return;
    const roundWasActive = metrics?.overtimeActive;
    const result = await executeTransaction(
      {
        busyKey: "overtime",
        pendingTitle: roundWasActive
          ? `Continue Overtime round ${metrics?.overtimeRound}`
          : "Start Overtime round",
        successDescription: (confirmed) =>
          roundWasActive
          ? `Overtime round ${metrics.overtimeRound} continued from its stored onchain cursor.`
          : `${amountLabel(confirmed.nativeAmount, 5)} ETH distributed through ${confirmed.routeCount} Overtime route.`,
        successTitle: roundWasActive
          ? "Overtime distribution continued"
          : "Overtime round started",
      },
      () => transactions.overtime(),
    );
    if (!result) return;
    await refreshAll();
  };

  const borrow = async () => {
    if (!requireSelectedBroker() || !transactions || !selectedToken) return;
    const result = await executeTransaction(
      {
        busyKey: "borrow",
        pendingTitle: `Borrow against Broker #${selectedToken}`,
        successDescription: () =>
          `Broker #${selectedToken} was collateralized and the full LAND principal was borrowed.`,
        successTitle: "Loan opened",
      },
      () =>
        transactions.borrow({
          tokenId: selectedToken,
          duration: BigInt(loanDurationDays) * 86400n,
        }),
    );
    if (!result) return;
    await refreshAll();
  };

  const repay = async () => {
    if (!requireLiveProtocol() || !transactions) return;
    if (!/^\d+$/.test(loanId)) {
      notifyWarning("Invalid loan ID", "Enter a numeric loan ID to repay.");
      return;
    }
    const result = await executeTransaction(
      {
        busyKey: "repay",
        pendingTitle: `Repay loan #${loanId}`,
        successDescription: () =>
          `Loan #${loanId} was repaid and its Broker collateral was returned.`,
        successTitle: "Loan repaid",
      },
      () => transactions.repay(BigInt(loanId)),
    );
    if (!result) return;
    await refreshAll();
  };

  const liquidate = async () => {
    if (!requireLiveProtocol() || !transactions) return;
    if (!/^\d+$/.test(loanId)) {
      notifyWarning(
        "Invalid loan ID",
        "Enter a numeric loan ID to liquidate.",
      );
      return;
    }
    const result = await executeTransaction(
      {
        busyKey: "liquidate",
        pendingTitle: `Liquidate loan #${loanId}`,
        successDescription: () =>
          `Expired loan #${loanId} was liquidated into AMM inventory.`,
        successTitle: "Loan liquidated",
      },
      () => transactions.liquidate(BigInt(loanId)),
    );
    if (!result) return;
    await refreshAll();
  };

  const shell = (
    <>
      <SiteHeader
        account={account}
        busy={busy}
        protocolLive={protocolLive}
        route={route}
        walletChainId={walletChainId}
        walletConfigured={walletConfigured}
        walletStatus={walletStatus}
        onWalletAction={() => void handleWalletAction()}
        onNavigate={navigate}
      />

      {route === "home" && <LandingPage />}

      {route === "collection" && <CollectionPage />}

      {route === "protocol" && <ProtocolPage />}

      {applicationRoute && (
        <main className="application-main">
          <AppPageHeader
            account={accountSnapshot}
            broker={broker}
            protocol={protocol}
            route={route}
          />

          {protocolLive && (
            <section className="route-content">
              {route === "portfolio" && (
                <TerminalView
                  account={account}
                  activationTiers={accountSnapshot?.activationTiers || {}}
                  activationTier={activationTier}
                  batchTokens={batchTokens}
                  broker={broker}
                  busy={busy}
                  currentTier={currentTier}
                  landBalance={landBalance}
                  metadata={brokerMetadata}
                  ownedTokens={ownedTokens}
                  portfolioAccount={portfolioAccount}
                  protocol={protocol}
                  protocolLive={transactionReady}
                  selectedTier={selectedTier}
                  selectedToken={selectedToken}
                  onActivate={activate}
                  onActivateBatch={activateBatch}
                  onSelectTier={setSelectedTier}
                  onSelectToken={setSelectedToken}
                  onToggleBatch={(tokenId) =>
                    setBatchTokens((current) =>
                      current.some((id) => id === tokenId)
                        ? current.filter((id) => id !== tokenId)
                        : [...current, tokenId],
                    )
                  }
                />
              )}

              {route === "trade" && (
                <BrokersView
                  account={account}
                  ammPrice={ammPrice}
                  busy={busy}
                  landBalance={landBalance}
                  metrics={metrics}
                  protocolLive={transactionReady}
                  selectedToken={selectedToken}
                  snipeToken={snipeToken}
                  snipeMetadata={snipeMetadata}
                  snipeQuote={snipeQuote}
                  onBuy={buyFromAmm}
                  onBuyLand={buyLand}
                  onSellLand={sellLand}
                  onSell={sellToAmm}
                  onSnipeToken={setSnipeToken}
                  onValidationError={(message) =>
                    notifyWarning("Invalid Broker ID", message)
                  }
                />
              )}

              {route === "rewards" && (
                <div className="personal-rewards-route">
                  <PersonalRewardsView
                    broker={broker}
                    metrics={metrics}
                    ownedTokens={ownedTokens}
                    selectedToken={selectedToken}
                    onSelectToken={setSelectedToken}
                  />
                  <details className="advanced-protocol-tools">
                    <summary>Advanced · run protocol emission rounds</summary>
                    <p>
                      These permissionless controls operate the protocol-wide
                      distribution process. Normal users do not need them to
                      receive rewards.
                    </p>
                    <RewardsView
                      activity={activity}
                      busy={busy}
                      clockInActive={metrics?.clockInActive}
                      clockInCursor={
                        metrics ? BigInt(metrics.clockInCursor) : undefined
                      }
                      clockInRecipientCount={
                        metrics
                          ? BigInt(metrics.clockInRecipientCount)
                          : undefined
                      }
                      clockInMinimum={clockInMinimum}
                      clockInPot={clockInPot}
                      clockInRound={
                        metrics ? BigInt(metrics.clockInRound) : undefined
                      }
                      emissionRouteCount={metrics?.emissionRouteCount}
                      overtimeActive={metrics?.overtimeActive}
                      overtimeCursor={
                        metrics ? BigInt(metrics.overtimeCursor) : undefined
                      }
                      overtimeRecipientCount={
                        metrics
                          ? BigInt(metrics.overtimeRecipientCount)
                          : undefined
                      }
                      overtimeMinimum={overtimeMinimum}
                      overtimePot={overtimePot}
                      overtimeRound={
                        metrics ? BigInt(metrics.overtimeRound) : undefined
                      }
                      protocolLive={transactionReady}
                      onClockIn={clockIn}
                      onOvertime={runOvertime}
                    />
                  </details>
                </div>
              )}

              {route === "loans" && (
                <LoansView
                  account={account}
                  busy={busy}
                  durationDays={loanDurationDays}
                  loanId={loanId}
                  loanPrincipal={
                    metrics ? BigInt(metrics.loanPrincipal) : undefined
                  }
                  loans={loans?.loans || []}
                  liveLoans={liveLoans}
                  loanApyBps={metrics?.loanApyBps}
                  borrowFee={
                    metrics
                      ? (BigInt(metrics.ammNotional) *
                          BigInt(metrics.loanApyBps) *
                          BigInt(loanDurationDays)) /
                        (365n * 10_000n)
                      : undefined
                  }
                  gracePeriod={
                    metrics ? BigInt(metrics.loanGracePeriod) : undefined
                  }
                  liquidationIncentiveBps={metrics?.liquidationIncentiveBps}
                  minDuration={
                    metrics ? BigInt(metrics.loanMinDuration) : undefined
                  }
                  maxDuration={
                    metrics ? BigInt(metrics.loanMaxDuration) : undefined
                  }
                  ownedTokens={ownedTokens}
                  protocolLive={transactionReady}
                  selectedToken={selectedToken}
                  onBorrow={borrow}
                  onDuration={setLoanDurationDays}
                  onLiquidate={liquidate}
                  onLoanId={setLoanId}
                  onRepay={repay}
                  onSelectToken={setSelectedToken}
                />
              )}
            </section>
          )}
        </main>
      )}

    </>
  );

  return <div className="app-shell">{shell}</div>;
}
